// Headless controller for the on-screen keyboard. No DOM access. Just
// layout data, a cursor, and the rules for what each key press means.
// The view layer turns this into pixels and actually writes characters
// into the focused input; this module tells it what to write.
//
// Two pages (ABC, SYM), both rows-of-keys. Each key is either:
//   { type: 'char', value: 'a' }. Print a character
//   { type: 'modal', value: 'shift' }. Toggle shift / caps
//   { type: 'modal', value: 'page' }. Swap page
//   { type: 'action', value: 'space'|'backspace'|'enter'|'close' }
// Run a named action

const SHIFT = { type: 'modal', value: 'shift' };
const PAGE  = { type: 'modal', value: 'page' };
const SPACE = { type: 'action', value: 'space', width: 6 };
const BS    = { type: 'action', value: 'backspace', width: 2 };
const ENT   = { type: 'action', value: 'enter', width: 2 };
const CLS   = { type: 'action', value: 'close', width: 2 };

function ch(v) { return { type: 'char', value: v }; }

const PAGE_ABC = [
  ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='].map(ch),
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']', '\\'].map(ch),
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', '\''].map(ch),
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/'].map(ch),
  [SHIFT, SPACE, PAGE, BS, ENT, CLS],
];

const PAGE_SYM = [
  ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '_', '+', '|'].map(ch),
  ['~', '{', '}', '[', ']', '<', '>', '/', '\\', ':', ';', '"', '\''].map(ch),
  ['€', '£', '¥', '°', '±', '×', '÷', '§', '¶', '·', '…'].map(ch),
  ['←', '→', '↑', '↓', '★', '♣', '♥', '♦', '♠', '⚔'].map(ch),
  [SHIFT, SPACE, PAGE, BS, ENT, CLS],
];

const LAYOUTS = { abc: PAGE_ABC, sym: PAGE_SYM };

function clamp(v, min, max) {
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

export function createVirtualKeyboardController() {
  let page = 'abc';
  let row = 0;
  let col = 0;
  let shift = false;
  let capsLock = false;
  let lastShiftAt = -Infinity;

  function keyAt(r, c) {
    const layout = LAYOUTS[page];
    const rowKeys = layout[r];
    if (!rowKeys) return null;
    return rowKeys[clamp(c, 0, rowKeys.length - 1)] || null;
  }

  function currentKey() { return keyAt(row, col); }

  function move(dir) {
    const layout = LAYOUTS[page];
    if (dir === 'up')   row = clamp(row - 1, 0, layout.length - 1);
    if (dir === 'down') row = clamp(row + 1, 0, layout.length - 1);
    const widthAtNewRow = layout[row].length;
    if (dir === 'left')  col = (col - 1 + widthAtNewRow) % widthAtNewRow;
    if (dir === 'right') col = (col + 1) % widthAtNewRow;
    col = clamp(col, 0, widthAtNewRow - 1);
  }

  function toggleShift(now = Date.now()) {
    if (capsLock) { capsLock = false; shift = false; return; }
    if (shift) {
      // Second press within 400ms → caps lock.
      if (now - lastShiftAt < 400) { capsLock = true; shift = true; return; }
      shift = false;
      return;
    }
    shift = true;
    lastShiftAt = now;
  }

  function togglePage() {
    page = (page === 'abc') ? 'sym' : 'abc';
    // Clamp cursor into the new page's row width.
    const layout = LAYOUTS[page];
    row = clamp(row, 0, layout.length - 1);
    col = clamp(col, 0, layout[row].length - 1);
  }

  // Consume the current key, returning an action record for the view
  // to execute. After consumption, auto-un-shifts (unless caps-lock).
  function pressCurrent() {
    const key = currentKey();
    if (!key) return null;
    if (key.type === 'char') {
      const value = shift ? key.value.toUpperCase() : key.value;
      if (shift && !capsLock) shift = false;
      return { type: 'char', value };
    }
    if (key.type === 'modal') {
      if (key.value === 'shift') { toggleShift(); return { type: 'modal', value: 'shift' }; }
      if (key.value === 'page')  { togglePage();  return { type: 'modal', value: 'page'  }; }
    }
    if (key.type === 'action') {
      return { type: 'action', value: key.value };
    }
    return null;
  }

  // Test helpers and view read-outs.
  function snapshot() {
    return {
      page, row, col, shift, capsLock,
      currentKey: currentKey(),
      layout: LAYOUTS[page],
    };
  }

  return {
    move,
    pressCurrent,
    toggleShift,
    togglePage,
    snapshot,
    setCursor(r, c) { row = r; col = c; },
  };
}

export { PAGE_ABC, PAGE_SYM, LAYOUTS };
